import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import api from '../../../api';
import DataTable from './DataTable';
import MetricCard from './MetricCard';
import RefreshTimer from './RefreshTimer';
import DetailModal, { historyDetail, queueDetail } from './DetailModal';

const employeeCols = [
    { key: 'EmployeeName', label: 'Employee Name' },
    { key: 'Direct', label: 'Direct' },
    { key: 'Indirect', label: 'Indirect' },
    { key: 'Description', label: 'Description' },
    { key: 'JobNum', label: 'Job Num' },
    { key: 'PartNum', label: 'Part Num' },
    { key: 'JobQty', label: 'Job Qty' },
    { key: 'StartTime', label: 'Start Time' },
];

const ShuklaTab = () => {
    const [employees, setEmployees] = useState([]);
    const [rma, setRma] = useState(null);
    const [incoming, setIncoming] = useState(null);
    const [dmr, setDmr] = useState(null);
    const [qfinal, setQfinal] = useState(null);
    const [avgWait, setAvgWait] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const loadedRef = useRef(false);
    const [detail, setDetail] = useState(null);   // Go-live parity (2026-09-24): legacy click-through popups

    const fetchData = useCallback(async () => {
        try {
            // Go-live parity (2026-09-24): refresh in the background — the
            // spinner shows only on the first load, so the TV no longer blanks
            // every minute (rows stay until the new ones arrive).
            if (!loadedRef.current) setLoading(true);
            setError(null);
            const res = await api.get('/display-board/shukla');
            const d = res.data;
            setEmployees(d?.employees || []);
            setRma(d?.rma || null);
            setIncoming(d?.incoming || null);
            setDmr(d?.dmr || null);
            setQfinal(d?.qfinal || null);
            setAvgWait(d?.avgWait || null);
            loadedRef.current = true;
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load Shukla data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    return (
        <>
            <Card className="border-0 shadow-sm mb-3">
                <CardHeader className="border-bottom d-flex justify-content-between align-items-center py-3">
                    <h6 className="mb-0 fw-semibold">
                        <i className="ri-building-2-line me-2 text-success"></i>
                        Shukla Department — Display Board
                    </h6>
                    <RefreshTimer onRefresh={fetchData} interval={60} />
                </CardHeader>
                <CardBody className="p-0">
                    {error && (
                        <div className="alert alert-danger m-3 mb-0" role="alert">
                            <i className="ri-error-warning-line me-2"></i>{error}
                        </div>
                    )}
                    <DataTable
                        onRowClick={(row) => setDetail(historyDetail(row))}
                        highlightKey="JobNum"  // Round DB-1 — green = active job
                        columns={employeeCols}
                        rows={employees}
                        loading={loading}
                        emptyMessage="No Shukla employees clocked in today"
                    />
                    <DetailModal request={detail} onClose={() => setDetail(null)} />
                </CardBody>
            </Card>

            <Row className="g-3">
                <Col md={6} lg={3}>
                    <MetricCard
                        title="RMA"
                        onClick={() => setDetail(queueDetail('shukla', 'rma', 'Shukla — Open RMAs'))}
                        color="danger"
                        icon="ri-exchange-line"
                        loading={loading}
                        metrics={rma ? [
                            { label: 'Open', value: rma.OpenRMA ?? rma.openRMA },
                            { label: 'Received', value: rma.Received ?? rma.received },
                            { label: 'Not Received', value: rma.NotReceived ?? rma.notReceived },
                            { label: 'Avg Wait Days', value: rma.AvgWaitDays ?? rma.avgWaitDays },
                        ] : null}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="Incoming Inspection"
                        onClick={() => setDetail(queueDetail('shukla', 'incoming', 'Shukla — Incoming Inspection Queue'))}
                        color="info"
                        icon="ri-inbox-archive-line"
                        loading={loading}
                        metrics={incoming ? [
                            { label: 'Count', value: incoming.IncomingCount ?? incoming.incomingCount },
                            { label: 'Avg Wait Days', value: incoming.AvgWaitDays ?? incoming.avgWaitDays },
                        ] : null}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="DMR"
                        onClick={() => setDetail(queueDetail('shukla', 'dmr', 'Shukla — Open DMRs'))}
                        color="warning"
                        icon="ri-file-warning-line"
                        loading={loading}
                        metrics={dmr ? [
                            { label: 'Count', value: dmr.DmrCount ?? dmr.dmrCount },
                            { label: 'Avg Wait Days', value: dmr.AvgWaitDays ?? dmr.avgWaitDays },
                        ] : null}
                    />
                </Col>
                <Col md={6} lg={3}>
                    <MetricCard
                        title="QFinal Queue"
                        onClick={() => setDetail(queueDetail('shukla', 'qfinal', 'Shukla — Final Inspection (QFinal) Queue'))}
                        color="primary"
                        icon="ri-check-double-line"
                        loading={loading}
                        metrics={qfinal ? [
                            { label: 'Count', value: qfinal.QfinalCount ?? qfinal.qfinalCount },
                            { label: 'Avg Wait Days', value: qfinal.AvgWaitDays ?? qfinal.avgWaitDays },
                        ] : null}
                    />
                </Col>
            </Row>

            {avgWait && (
                <Row className="g-3 mt-1">
                    <Col md={4}>
                        <MetricCard
                            // Go-live parity (2026-09-24): the roster is every active
                            // dept-59 employee (owner decision, Round DB-2), so say so.
                            title="Avg. Turnaround — Dept 59 (Last 100)"
                            color="secondary"
                            icon="ri-timer-line"
                            loading={loading}
                            metrics={[{ label: 'Avg Wait Days', value: avgWait.AvgWaitDays ?? avgWait.avgWaitDays }]}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

export default ShuklaTab;